/**
 * Telegram Bot Service
 * Generates deep-links for bot connection and checks connection status via Supabase.
 * Actual message parsing happens server-side in the Telegram webhook Edge Function.
 */

import { supabase } from "@/integrations/supabase/client";

export interface TelegramSettings {
  user_id: string;
  telegram_chat_id: string;
  telegram_username: string | null;
  telegram_first_name: string | null;
  is_connected: boolean;
  notifications_enabled: boolean;
  daily_reminders: boolean;
  created_at: string;
  updated_at: string;
}

export const TelegramBotService = {
  getBotUsername(): string {
    return import.meta.env.VITE_TELEGRAM_BOT_USERNAME || "MyhuCarbonBot";
  },

  /**
   * Returns the t.me deep-link that opens the bot and passes the Supabase user ID
   * as the /start payload.  The bot's Edge Function reads this payload to link the
   * Telegram chat ID to the Myhu account.
   */
  getConnectLink(userId: string): string {
    const bot = this.getBotUsername();
    // Sanitise: only alphanumeric + hyphens should appear in a UUID
    const safeUserId = userId.replace(/[^a-zA-Z0-9\-]/g, "");
    return `https://t.me/${bot}?start=${safeUserId}`;
  },

  async fetchSettings(userId: string): Promise<TelegramSettings | null> {
    const { data, error } = await supabase
      .from("telegram_settings")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle();

    if (error) {
      console.error("Error fetching Telegram settings:", error);
      return null;
    }
    return data as TelegramSettings | null;
  },

  async disconnect(userId: string): Promise<void> {
    const { error } = await supabase
      .from("telegram_settings")
      .delete()
      .eq("user_id", userId);

    if (error) throw error;
  },

  async updateSetting(
    userId: string,
    field: "notifications_enabled" | "daily_reminders",
    value: boolean,
  ): Promise<void> {
    const { error } = await supabase
      .from("telegram_settings")
      .update({ [field]: value, updated_at: new Date().toISOString() })
      .eq("user_id", userId);

    if (error) throw error;
  },
};
